import { AssetType } from '@tradingblock/types';
import { AggregatedPositionDTO, PositionDTO, PartialQuote } from '@tradingblock/api';
import _ from 'lodash';

export function avgPositionStrat(positions: (PositionDTO | AggregatedPositionDTO)[]): number {
  if (!positions || positions.length === 0) {
    return 0;
  }

  // Separate stocks and options
  const stockPositions = positions.filter(p => p.AssetType === AssetType.Equity);
  const optionPositions = positions.filter(p => p.AssetType === AssetType.Option);

  // Calculate stock positions
  let stockCostBasis = 0;
  let stockQuantity = 0;
  for (const position of stockPositions) {
    const { OpenPrice, OpenQuantity } = position;
    stockCostBasis += OpenPrice * OpenQuantity;
    stockQuantity += OpenQuantity;
  }

  // Calculate options positions
  let optionsCostBasis = 0;
  let optionsQuantity = 0;
  for (const position of optionPositions) {
    const { OpenPrice, OpenQuantity } = position;
    // Don't multiply by OptionMultiplier here since it will be applied later in calculatePositionCostValue
    optionsCostBasis += OpenPrice * OpenQuantity;
    optionsQuantity += OpenQuantity;
  }

  // If we have both stocks and options, we need to keep them separate
  if (stockPositions.length > 0 && optionPositions.length > 0) {
    // Return stock average if we're looking at stocks, options average if looking at options
    return positions[0].AssetType === AssetType.Equity
      ? stockCostBasis / stockQuantity
      : optionsCostBasis / optionsQuantity;
  }

  // If we only have one type, return its average
  const totalCostBasis = stockCostBasis + optionsCostBasis;
  const totalQuantity = stockQuantity + optionsQuantity;

  if (totalQuantity === 0) {
    return 0;
  }

  return totalCostBasis / totalQuantity;
}

/**
 * Calculates the position cost value based on the average price, the number of contracts and the multiplier (100 - option | 1 - share).
 * @param avgPrice - number | the average price
 * @param numOfContracts - number | the number of contracts
 * @param assetType - AssetType | the asset type
 * @param multiplier - number (optional) | the multiplier
 * @returns - number | the position cost value
 */
export const calculatePositionCostValue = (
  avgPrice: number,
  numOfContracts: number,
  assetType: AssetType,
  multiplier?: number
): number => {
  // We are setting another multiplier based on the asset type to create a fallback in the case the multiplier is not available from the backend.
  let multiplierFromAssetType = 1;

  if (assetType === AssetType.Option) {
    multiplierFromAssetType = 100;
  }

  let value = avgPrice * numOfContracts * (multiplier || multiplierFromAssetType);

  if (Number.isNaN(value)) {
    return 0;
  } else {
    return value;
  }
};

export function calculatePositionGain(
  positions: (PositionDTO | AggregatedPositionDTO)[],
  currentValue: number,
  rawGain: number
): number {
  // Input validation
  if (!positions || positions.length === 0 || typeof currentValue !== 'number' || isNaN(currentValue)) {
    return 0;
  }

  // Money Market positions always return 0 gain
  if (positions[0].AssetType === AssetType.MoneyMarket) {
    return 0;
  }

  // Single position case
  if (positions.length === 1) {
    const costValue = calculatePositionCostValue(
      positions[0].OpenPrice,
      positions[0].OpenQuantity,
      positions[0].AssetType,
      positions[0].OptionMultiplier
    );

    return currentValue - costValue;
  }

  // Multiple positions with same symbol
  const sameSymbol = positions.every(pos => pos.Symbol === positions[0].Symbol);
  if (sameSymbol) {
    const avgPrice = avgPositionStrat(positions);
    const totalQuantity = positions.reduce((sum, pos) => sum + pos.OpenQuantity, 0);

    const costValue = calculatePositionCostValue(
      avgPrice,
      totalQuantity,
      positions[0].AssetType,
      positions[0].OptionMultiplier
    );

    return currentValue - costValue;
  }

  // For multiple positions with different symbols, use the raw gain
  return rawGain;
}

export function calculateTotalPositionCost(positions: (PositionDTO | AggregatedPositionDTO)[]): number {
  if (!positions || positions.length === 0) {
    return 0;
  }

  if (positions.length === 1) {
    return calculatePositionCostValue(
      avgPositionStrat(positions),
      positions[0].OpenQuantity,
      positions[0].AssetType,
      positions[0].OptionMultiplier
    );
  }

  // Always calculate cost without commission, regardless of symbol
  const stockPositions = positions.filter(p => p.AssetType === AssetType.Equity);
  const optionPositions = positions.filter(p => p.AssetType === AssetType.Option);

  // Calculate stock cost - handle each position separately
  const stockCost = stockPositions.reduce((sum, position) => {
    return (
      sum +
      calculatePositionCostValue(
        position.OpenPrice,
        Math.abs(position.OpenQuantity),
        position.AssetType,
        position.OptionMultiplier
      )
    );
  }, 0);

  // Calculate options cost
  const optionsCost = optionPositions.reduce((sum, position) => {
    // For options, use the actual sign of OpenQuantity to handle spreads correctly
    return (
      sum +
      calculatePositionCostValue(
        position.OpenPrice,
        position.OpenQuantity, // Remove Math.abs() to preserve the sign
        position.AssetType,
        position.OptionMultiplier
      )
    );
  }, 0);

  return stockCost + optionsCost;
}

export function calculateDisplayGain(
  positions: (PositionDTO | AggregatedPositionDTO)[],
  currentValue: number,
  totalGain: number,
  assetType: AssetType
): number {
  const sameUnderlying =
    positions.length > 1 && positions.every(pos => pos.UnderlyingSymbol === positions[0].UnderlyingSymbol);

  if (!positions || positions.length === 0 || assetType === AssetType.MoneyMarket) {
    return 0;
  }

  // For single positions, use direct calculation
  if (positions.length === 1) {
    const costValue = calculatePositionCostValue(
      avgPositionStrat(positions),
      positions[0].OpenQuantity,
      positions[0].AssetType,
      positions[0].OptionMultiplier
    );

    return currentValue - costValue;
  }

  // For multiple positions of same underlying (including mixed stock/options),
  // calculate gain directly to avoid commission issues
  if (sameUnderlying) {
    const cost = calculateTotalPositionCost(positions);
    return currentValue - cost;
  }

  // For different underlying symbols, use provided totalGain
  return totalGain;
}
